/** @file Basic styling wrapper for a merchant config page */

/* eslint-disable react/jsx-props-no-spreading */

import React from 'react';
import {
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Box,
  BoxProps,
  SxProps,
} from '@mui/material';
import HierarchyBreadcrumbs from '../components/core/HierarchyBreadcrumbs';
import { InheritedSettingsBannerProvider } from '../contexts/InheritedSettingsBannerContext';
import { ParentHierarchySettingsBanner } from './ParentHierarchySettingsBanner';

const styles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 0,
  mt: 3,
  pl: { xs: 8, md: 13, lg: 15 },
  pr: { xs: 5, sm: 8, md: 13, lg: 15 },
  mb: { xs: 2, md: 3, lg: 4 },
};

interface Props extends BoxProps {
  /** Page contents */
  children: React.ReactNode;
  /** Optional style overrides for base Box component */
  sx?: SxProps;
  title: string;
  description: React.ReactNode;
  isLoading?: boolean;
  withDivider?: boolean;
  /** When true, show an info banner if the current merchant has descendants (parent in hierarchy). */
  showParentHierarchyBanner?: boolean;
  /** Optional override for the parent-hierarchy info banner (e.g. webhooks). */
  parentHierarchyBannerMessage?: string;
}

/**
 * Styled page wrapper
 * @param {Object} props Component properties
 * @param {string} props.title Page title
 * @param {string} props.description Page title
 * @returns {JSX.Element} Page title + description
 */
const PageWrapper = ({
  children,
  sx,
  title,
  description,
  isLoading = false,
  withDivider = true,
  showParentHierarchyBanner = false,
  parentHierarchyBannerMessage,
  ...PropsForBox
}: Props) => {
  return (
    <InheritedSettingsBannerProvider>
      <Box {...PropsForBox} sx={{ ...styles, ...sx }} component="section">
        <HierarchyBreadcrumbs />
        <Stack spacing={1} sx={{ mb: 2, mt: { xs: 2, md: 3 } }}>
          <Typography variant="huge" fontWeight="semibold">
            {title}
          </Typography>
          <Typography variant="medium" color="text.secondary">
            {description}
          </Typography>
        </Stack>
        {withDivider && <Divider sx={{ mb: 3 }} />}
        {showParentHierarchyBanner && !isLoading && (
          <ParentHierarchySettingsBanner message={parentHierarchyBannerMessage} />
        )}
        {isLoading && (
          <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress aria-label="Loading" />
          </Box>
        )}
        {!isLoading && children}
      </Box>
    </InheritedSettingsBannerProvider>
  );
};

export default PageWrapper;
